import { useState, useEffect } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, Lock, LockOpen, Link2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type WorkflowItem = {
  id: string;
  category: string;
  appliance_type: string;
  brand: string | null;
  title: string;
  description: string | null;
  sort_order: number;
  is_mandatory: boolean;
  estimated_minutes?: number | null;
};

interface DraggableWorkflowItemProps {
  item: WorkflowItem;
  categoryKey: string;
  isRepairChecked: boolean;
  isRefurbishChecked: boolean;
  isRepairInherited?: boolean;
  isRefurbishInherited?: boolean;
  selectedBrand: string;
  isAllBrands: boolean;
  onToggleDefault: (itemId: string, type: "repair" | "refurbish") => void;
  onDelete: (id: string) => void;
  onToggleMandatory: (id: string) => void;
  mappingCount?: number;
  onOpenMappings?: (item: WorkflowItem) => void;
  estimatedMinutes: number | null;
  isMinutesInherited?: boolean;
  onChangeMinutes: (itemId: string, minutes: number | null) => void;
}

export function DraggableWorkflowItem({
  item,
  categoryKey,
  isRepairChecked,
  isRefurbishChecked,
  isRepairInherited = false,
  isRefurbishInherited = false,
  selectedBrand,
  isAllBrands,
  onToggleDefault,
  onDelete,
  onToggleMandatory,
  mappingCount = 0,
  onOpenMappings,
  estimatedMinutes,
  isMinutesInherited = false,
  onChangeMinutes,
}: DraggableWorkflowItemProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [minutesDraft, setMinutesDraft] = useState<string>(
    estimatedMinutes != null ? String(estimatedMinutes) : ""
  );

  useEffect(() => {
    setMinutesDraft(estimatedMinutes != null ? String(estimatedMinutes) : "");
  }, [estimatedMinutes]);

  const commitMinutes = () => {
    const trimmed = minutesDraft.trim();
    if (trimmed === "") {
      if (estimatedMinutes != null) onChangeMinutes(item.id, null);
      return;
    }
    const num = Number(trimmed);
    if (!Number.isFinite(num) || num < 0) {
      setMinutesDraft(estimatedMinutes != null ? String(estimatedMinutes) : "");
      return;
    }
    if (num !== estimatedMinutes) onChangeMinutes(item.id, num);
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    data: { category: categoryKey, item },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  const canDelete = isAllBrands || !item.is_mandatory;

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className={`grid grid-cols-[28px_1fr_60px_60px_72px_28px_36px_36px] gap-2 px-4 py-3 border-b border-border last:border-b-0 items-center ${
          isDragging ? "bg-accent/50 rounded-lg shadow-lg" : ""
        }`}
      >
        <button
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-foreground">{item.title}</p>
            {item.is_mandatory && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-destructive/10 text-destructive font-medium">
                mandatory
              </span>
            )}
            {categoryKey === "tips" && item.brand && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                {item.brand}
              </span>
            )}
          </div>
          {item.description && (
            <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
          )}
        </div>
        <div className="flex justify-center relative">
          <Checkbox
            checked={isRepairChecked}
            onCheckedChange={() => onToggleDefault(item.id, "repair")}
            disabled={!selectedBrand}
            className={isRepairInherited && !isAllBrands ? "border-primary/40 data-[state=checked]:bg-primary/40" : ""}
          />
          {isRepairInherited && !isAllBrands && (
            <span className="absolute -bottom-3 text-[8px] text-muted-foreground">parent</span>
          )}
        </div>
        <div className="flex justify-center relative">
          <Checkbox
            checked={isRefurbishChecked}
            onCheckedChange={() => onToggleDefault(item.id, "refurbish")}
            disabled={!selectedBrand}
            className={isRefurbishInherited && !isAllBrands ? "border-primary/40 data-[state=checked]:bg-primary/40" : ""}
          />
          {isRefurbishInherited && !isAllBrands && (
            <span className="absolute -bottom-3 text-[8px] text-muted-foreground">parent</span>
          )}
        </div>
        <div className="flex justify-center relative">
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={minutesDraft}
            onChange={(e) => setMinutesDraft(e.target.value)}
            onBlur={commitMinutes}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            placeholder={isMinutesInherited ? "—" : "min"}
            title={isMinutesInherited ? "Inherited from All Brands. Enter a value to override." : "Estimated minutes for this step"}
            className={`h-7 px-2 text-xs text-center ${isMinutesInherited ? "text-muted-foreground border-dashed" : ""}`}
          />
        </div>
        <div className="flex justify-center">
          {isAllBrands ? (
            <button
              onClick={() => onToggleMandatory(item.id)}
              className={`transition-colors ${item.is_mandatory ? "text-destructive hover:text-destructive/70" : "text-muted-foreground hover:text-foreground"}`}
              title={item.is_mandatory ? "Remove mandatory flag" : "Mark as mandatory"}
            >
              {item.is_mandatory ? <Lock className="w-3.5 h-3.5" /> : <LockOpen className="w-3.5 h-3.5" />}
            </button>
          ) : item.is_mandatory ? (
            <Lock className="w-3.5 h-3.5 text-muted-foreground/50" />
          ) : null}
        </div>
        <div className="flex justify-center">
          {categoryKey === "error_codes" && onOpenMappings ? (
            <button
              onClick={() => onOpenMappings(item)}
              className={`transition-colors relative ${mappingCount > 0 ? "text-primary hover:text-primary/70" : "text-muted-foreground hover:text-foreground"}`}
              title={`Link to troubleshooting/parts (${mappingCount} linked)`}
            >
              <Link2 className="w-3.5 h-3.5" />
              {mappingCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-primary text-primary-foreground text-[8px] flex items-center justify-center font-bold">
                  {mappingCount}
                </span>
              )}
            </button>
          ) : null}
        </div>
        <div className="flex justify-center">
          <button
            onClick={() => canDelete && setShowDeleteDialog(true)}
            className={`transition-colors ${canDelete ? "text-muted-foreground hover:text-destructive" : "text-muted-foreground/30 cursor-not-allowed"}`}
            disabled={!canDelete}
            title={!canDelete ? "Mandatory step — cannot delete from brand view" : "Delete"}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete step</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-medium text-foreground">"{item.title}"</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete(item.id);
                setShowDeleteDialog(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
